export type AdvancedColoringRegion = { id: string; d: string };
export type AdvancedColoringLesson = {
  id: string;
  title: string;
  subject: string;
  difficulty: "L3" | "L4";
  icon: string;
};

const circle = (id: string, cx: number, cy: number, radius: number): AdvancedColoringRegion => ({
  id,
  d: `M${cx - radius} ${cy} A${radius} ${radius} 0 1 0 ${cx + radius} ${cy} A${radius} ${radius} 0 1 0 ${cx - radius} ${cy}`,
});

const petalRing = (prefix: string, count: number, cx: number, cy: number, inner: number, outer: number) =>
  Array.from({ length: count }, (_, index) => {
    const angle = (Math.PI * 2 * index) / count - Math.PI / 2;
    const spread = Math.PI / count * .62;
    const p1 = [cx + Math.cos(angle - spread) * inner, cy + Math.sin(angle - spread) * inner];
    const p2 = [cx + Math.cos(angle) * outer, cy + Math.sin(angle) * outer];
    const p3 = [cx + Math.cos(angle + spread) * inner, cy + Math.sin(angle + spread) * inner];
    return { id: `${prefix}-${index + 1}`, d: `M${cx} ${cy} C${p1[0]} ${p1[1]} ${p2[0]} ${p2[1]} ${p2[0]} ${p2[1]} C${p2[0]} ${p2[1]} ${p3[0]} ${p3[1]} ${cx} ${cy}Z` };
  });

const flower = (prefix: string, cx: number, cy: number, scale = 1) => [
  ...petalRing(`${prefix}-petal`, 6, cx, cy, 18 * scale, 62 * scale),
  circle(`${prefix}-center`, cx, cy, 20 * scale),
];

const stillLife: AdvancedColoringRegion[] = [
  {id:"table",d:"M55 385 H645 V455 H55Z"},{id:"vase",d:"M285 205 H415 L390 410 H310Z"},{id:"vase-band",d:"M300 290 H400 L395 345 H305Z"},
  {id:"stem-1",d:"M325 215 C300 145 230 115 195 95 L207 85 C260 110 330 155 340 215Z"},{id:"stem-2",d:"M350 215 C350 150 350 105 350 65 H365 C365 115 365 165 365 215Z"},{id:"stem-3",d:"M380 215 C405 150 465 125 520 100 L528 112 C475 140 420 165 395 220Z"},
  ...flower("left",185,90,.72),...flower("middle",355,62,.8),...flower("right",530,100,.72),
  {id:"leaf-1",d:"M295 170 C220 115 185 195 300 205Z"},{id:"leaf-2",d:"M410 170 C500 110 530 205 405 215Z"},{id:"apple",d:"M105 340 C105 270 205 270 205 340 C205 415 105 415 105 340Z"},{id:"pear",d:"M535 320 C565 270 620 300 615 360 C610 420 510 420 505 360 C500 325 515 305 535 320Z"}
];

const mountainScene: AdvancedColoringRegion[] = [
  {id:"sky",d:"M50 45 H650 V250 H50Z"},{id:"sun",d:"M90 105 A48 48 0 1 0 186 105 A48 48 0 1 0 90 105"},
  {id:"mountain-back",d:"M55 300 L225 105 L350 245 L470 90 L650 300Z"},{id:"snow-1",d:"M165 175 L225 105 L285 175 L245 160 L225 185 L205 160Z"},{id:"snow-2",d:"M405 165 L470 90 L535 165 L495 145 L470 175 L445 145Z"},
  {id:"river",d:"M310 245 C230 310 420 335 255 455 H470 C515 365 370 330 430 245Z"},{id:"grass",d:"M50 300 C170 270 230 330 330 290 C430 250 520 315 650 280 V455 H50Z"},
  {id:"tree-1",d:"M95 280 C55 220 110 165 145 210 C175 150 240 215 190 265 C220 320 135 345 95 280Z"},{id:"trunk-1",d:"M125 275 H155 V385 H125Z"},{id:"tree-2",d:"M500 290 C460 220 520 175 555 220 C595 160 645 230 605 275 C640 335 540 345 500 290Z"},{id:"trunk-2",d:"M540 285 H570 V395 H540Z"},
  ...Array.from({length:8},(_,i)=>circle(`cloud-${i}`,250+i*28,92+(i%2)*10,24))
];

const birdGarden: AdvancedColoringRegion[] = [
  {id:"body",d:"M235 265 C250 120 480 110 500 260 C515 405 255 420 215 315Z"},{id:"head",d:"M390 125 C440 45 570 85 555 185 C540 275 420 270 385 205Z"},{id:"wing",d:"M250 245 C310 145 435 195 430 305 C375 365 275 340 250 245Z"},{id:"beak",d:"M545 155 L635 195 L545 215Z"},{id:"tail-1",d:"M230 300 L85 235 L180 355Z"},{id:"tail-2",d:"M215 325 L75 340 L200 390Z"},{id:"eye",d:"M485 145 A18 18 0 1 0 521 145 A18 18 0 1 0 485 145"},
  ...flower("garden-a",145,405,.55),...flower("garden-b",315,420,.5),...flower("garden-c",520,405,.55),
  {id:"branch",d:"M75 430 C230 385 425 440 635 365 L640 390 C430 455 240 415 80 455Z"}
];

const bakery: AdvancedColoringRegion[] = [
  {id:"wall",d:"M80 95 H620 V440 H80Z"},{id:"roof",d:"M50 95 H650 L600 35 H100Z"},
  ...Array.from({length:7},(_,i)=>({id:`awning-${i}`,d:`M${80+i*77} 95 H${157+i*77} L${145+i*77} 170 H${92+i*77}Z`})),
  {id:"window",d:"M125 190 H430 V385 H125Z"},{id:"door",d:"M460 185 H575 V440 H460Z"},{id:"sign",d:"M235 40 H465 V105 H235Z"},
  {id:"cake-1",d:"M155 300 H245 V365 H155Z"},{id:"cream-1",d:"M155 300 C175 260 205 330 225 285 C245 260 255 285 245 300Z"},
  {id:"bread",d:"M280 315 C280 250 390 250 390 315 V365 H280Z"},{id:"handle",d:"M520 300 A15 15 0 1 0 550 300 A15 15 0 1 0 520 300"}
];

const oceanFamily: AdvancedColoringRegion[] = [
  {id:"water",d:"M45 45 H655 V455 H45Z"},{id:"fish-body",d:"M165 235 C230 120 440 125 495 235 C440 350 230 355 165 235Z"},{id:"fish-tail",d:"M165 235 L65 135 L75 345Z"},{id:"fish-fin",d:"M300 150 L360 75 L410 165Z"},{id:"fish-eye",d:"M415 195 A20 20 0 1 0 455 195 A20 20 0 1 0 415 195"},
  ...Array.from({length:8},(_,i)=>circle(`bubble-${i}`,500+(i%3)*42,70+Math.floor(i/3)*48,12+(i%2)*6)),
  ...Array.from({length:6},(_,i)=>({id:`coral-${i}`,d:`M${95+i*92} 455 C${75+i*92} 390 ${120+i*92} 365 ${105+i*92} 310 C${145+i*92} 355 ${155+i*92} 395 ${145+i*92} 455Z`}))
];

const spaceLab: AdvancedColoringRegion[] = [
  {id:"space",d:"M45 45 H655 V455 H45Z"},{id:"rocket",d:"M285 330 C275 205 310 85 350 50 C390 85 425 205 415 330Z"},{id:"window",d:"M310 175 A40 40 0 1 0 390 175 A40 40 0 1 0 310 175"},{id:"wing-1",d:"M285 255 C205 285 195 355 275 340Z"},{id:"wing-2",d:"M415 255 C495 285 505 355 425 340Z"},{id:"flame-1",d:"M305 330 C300 430 340 455 350 365 C360 455 400 430 395 330Z"},
  ...Array.from({length:10},(_,i)=>circle(`planet-${i}`,85+(i%5)*130,90+Math.floor(i/5)*290,16+(i%3)*8))
];

const mandala: AdvancedColoringRegion[] = [circle("center",350,240,38),...petalRing("inner",12,350,240,35,120),...petalRing("outer",20,350,240,105,220)];
const peacock: AdvancedColoringRegion[] = [
  ...petalRing("feather",18,350,350,65,285),{id:"body",d:"M285 220 C300 115 400 115 415 220 L455 440 H245Z"},{id:"neck",d:"M325 230 C285 125 310 55 365 55 C430 55 445 145 390 185 L385 260Z"},{id:"head",d:"M325 85 A45 45 0 1 0 415 85 A45 45 0 1 0 325 85"},
  ...Array.from({length:9},(_,i)=>circle(`eye-feather-${i}`,145+i*52,165+(i%2)*70,16))
];
const fantasyCastle: AdvancedColoringRegion[] = [
  {id:"wall",d:"M115 260 H585 V445 H115Z"},{id:"gate",d:"M300 330 C300 245 400 245 400 330 V445 H300Z"},
  ...Array.from({length:5},(_,i)=>({id:`tower-${i}`,d:`M${95+i*118} ${180-(i%2)*55} H${185+i*118} V445 H${95+i*118}Z`})),
  ...Array.from({length:5},(_,i)=>({id:`roof-${i}`,d:`M${75+i*118} ${180-(i%2)*55} L${140+i*118} ${65-(i%2)*35} L${205+i*118} ${180-(i%2)*55}Z`})),
  ...Array.from({length:10},(_,i)=>({id:`window-${i}`,d:`M${120+(i%5)*118} ${225+Math.floor(i/5)*90} H${155+(i%5)*118} V${270+Math.floor(i/5)*90} H${120+(i%5)*118}Z`}))
];
const coralPalace: AdvancedColoringRegion[] = [
  {id:"palace",d:"M185 225 H515 V440 H185Z"},{id:"dome",d:"M185 225 C210 75 490 75 515 225Z"},{id:"door",d:"M305 330 C305 250 395 250 395 330 V440 H305Z"},
  ...Array.from({length:12},(_,i)=>circle(`pearl-${i}`,100+(i%6)*100,80+Math.floor(i/6)*300,18+(i%2)*8)),
  ...Array.from({length:10},(_,i)=>({id:`coral-palace-${i}`,d:`M${65+i*63} 455 C${45+i*63} 380 ${90+i*63} 350 ${75+i*63} 295 C${120+i*63} 350 ${125+i*63} 405 ${110+i*63} 455Z`}))
];
const dragonSky: AdvancedColoringRegion[] = [
  {id:"dragon-body",d:"M105 330 C145 95 390 70 485 210 C585 360 410 445 285 350 C220 300 165 370 105 330Z"},{id:"head",d:"M430 175 C545 95 650 215 555 310 L465 275Z"},{id:"wing-a",d:"M285 205 L205 45 L365 145 L455 55 L445 255Z"},{id:"wing-b",d:"M260 250 L135 145 L185 310Z"},{id:"horn-a",d:"M475 165 L500 70 L535 175Z"},{id:"horn-b",d:"M520 175 L575 95 L570 210Z"},
  ...Array.from({length:16},(_,i)=>circle(`scale-${i}`,205+(i%8)*38,265+Math.floor(i/8)*48,14))
];
const dreamStage: AdvancedColoringRegion[] = [
  {id:"curtain-left",d:"M55 45 H225 C175 180 175 340 235 455 H55Z"},{id:"curtain-right",d:"M475 45 H645 V455 H465 C525 340 525 180 475 45Z"},{id:"stage",d:"M160 360 H540 L615 455 H85Z"},{id:"arch",d:"M160 360 V185 C160 45 540 45 540 185 V360 H500 V190 C500 95 200 95 200 190 V360Z"},
  ...petalRing("stage-star",12,350,225,35,125),...Array.from({length:8},(_,i)=>circle(`lamp-${i}`,155+i*56,65,15))
];

export const advancedColoringLessons: AdvancedColoringLesson[] = [
  {id:"still-life",title:"花瓶与水果静物",subject:"advanced-still-life",difficulty:"L3",icon:"🍎"},
  {id:"mountain-landscape",title:"远山与河流风景",subject:"advanced-mountain",difficulty:"L3",icon:"🏞"},
  {id:"bird-garden",title:"花园里的小鸟",subject:"advanced-bird",difficulty:"L3",icon:"🐦"},
  {id:"bakery-window",title:"街角面包店",subject:"advanced-bakery",difficulty:"L3",icon:"🥐"},
  {id:"ocean-family",title:"热闹的海底世界",subject:"advanced-ocean",difficulty:"L3",icon:"🐠"},
  {id:"space-lab",title:"星际科学实验室",subject:"advanced-space",difficulty:"L3",icon:"🛰"},
  {id:"mandala-pro",title:"星辉曼陀罗",subject:"pro-mandala",difficulty:"L4",icon:"❈"},
  {id:"peacock-pro",title:"孔雀星羽屏",subject:"pro-peacock",difficulty:"L4",icon:"🦚"},
  {id:"castle-pro",title:"云端幻想城堡",subject:"pro-castle",difficulty:"L4",icon:"🏰"},
  {id:"coral-palace-pro",title:"珊瑚海底宫殿",subject:"pro-coral",difficulty:"L4",icon:"🪸"},
  {id:"dragon-pro",title:"飞越云海的龙",subject:"pro-dragon",difficulty:"L4",icon:"🐉"},
  {id:"stage-pro",title:"星光梦想大舞台",subject:"pro-stage",difficulty:"L4",icon:"🎭"},
];

export const advancedColoringDesigns: Record<string, AdvancedColoringRegion[]> = {
  "advanced-still-life": stillLife,
  "advanced-mountain": mountainScene,
  "advanced-bird": birdGarden,
  "advanced-bakery": bakery,
  "advanced-ocean": oceanFamily,
  "advanced-space": spaceLab,
  "pro-mandala": mandala,
  "pro-peacock": peacock,
  "pro-castle": fantasyCastle,
  "pro-coral": coralPalace,
  "pro-dragon": dragonSky,
  "pro-stage": dreamStage,
};
